import { useState, useCallback, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/axiosConfig';
import { Label } from './ui/label';
import { Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

const FUN_EMOJIS = ['😫', '😕', '😐', '🙂', '😄'];

/**
 * Fun factor (1–5) + session RPE 0–10 (Foster CR-10) for training analytics.
 */
export default function ActivitySessionFeedback({ activityId, funFactor, sessionRpe, onSaved }) {
  const { t } = useLanguage();
  const [fun, setFun] = useState(funFactor != null ? Number(funFactor) : null);
  const [rpe, setRpe] = useState(sessionRpe != null ? Number(sessionRpe) : null);
  const [saving, setSaving] = useState(false);

  const funRef = useRef(fun);
  const rpeRef = useRef(rpe);
  funRef.current = fun;
  rpeRef.current = rpe;

  const persistFeedback = useCallback(
    async (nextFun, nextRpe) => {
      if (!activityId) return;
      setSaving(true);
      try {
        await api.put(`/api/strava/activity/${activityId}/feedback`, {
          funFactor: nextFun,
          sessionRpe: nextRpe,
        });
        onSaved?.();
      } catch (e) {
        console.error(e);
      } finally {
        setSaving(false);
      }
    },
    [activityId, onSaved]
  );

  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-sm font-semibold text-foreground">{t('activityFeedback.sessionTitle')}</h3>
        {saving ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" aria-hidden /> : null}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-6 items-start">
          <div className="min-w-0">
            <Label className="text-xs text-muted-foreground">{t('activityFeedback.funLabel')}</Label>
            <div className="mt-2 flex flex-wrap gap-1">
              {FUN_EMOJIS.map((emoji, i) => {
                const v = i + 1;
                const selected = fun === v;
                return (
                  <button
                    key={v}
                    type="button"
                    disabled={saving || !activityId}
                    aria-pressed={selected}
                    aria-label={t('activityFeedback.funAria', { level: v })}
                    onClick={() => {
                      const next = selected ? null : v;
                      funRef.current = next;
                      setFun(next);
                      void persistFeedback(next, rpeRef.current);
                    }}
                    className={cn(
                      'text-2xl leading-none rounded-lg p-2 transition-colors border',
                      selected
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/30'
                        : 'border-transparent bg-muted/40 hover:bg-muted/80'
                    )}
                  >
                    {emoji}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="min-w-0">
            <Label className="text-xs text-muted-foreground">{t('activityFeedback.srpeLabel')}</Label>
            <div className="mt-2 flex flex-wrap gap-1">
              {Array.from({ length: 11 }, (_, i) => i).map((n) => {
                const selected = rpe === n;
                return (
                  <button
                    key={n}
                    type="button"
                    disabled={saving || !activityId}
                    aria-pressed={selected}
                    aria-label={t('activityFeedback.srpeAria', { value: n })}
                    onClick={() => {
                      const next = selected ? null : n;
                      rpeRef.current = next;
                      setRpe(next);
                      void persistFeedback(funRef.current, next);
                    }}
                    className={cn(
                      'min-w-[2rem] rounded-md border px-2 py-1 text-xs font-medium tabular-nums transition-colors',
                      selected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-muted/50 text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">{t('activityFeedback.srpeHint')}</p>
          </div>
        </div>
    </div>
  );
}
